import { useCallback } from 'react';
// Api
import { StreamStatus } from 'api/graphql-global-types';
// Constants
import { STREAM_CHECKOUT } from 'constants/routes';
// Helpers
import {
  computeOrderStreamPath,
  computeVideoWatchStreamPath,
} from 'helpers/routes';

export function useStreamNavigation(loggedUserId: string | null) {
  const getStreamNavigationProps = useCallback(
    (stream, toCheckout?) => {
      const isScheduled = stream.streamStatus === StreamStatus.Scheduled;
      const isEnded = stream.streamStatus === StreamStatus.Ended;
      const canWatch =
        stream.isFree ||
        stream.isPurchased ||
        stream.store?.id === loggedUserId;

      // Logged-in users buying a paid stream they can't yet watch go straight
      // to the checkout screen. This must take priority over the scheduled and
      // ended checks below so the "Buy PPV" action never lands on the details
      // page.
      if (toCheckout && loggedUserId && !stream?.isFree && !canWatch) {
        return {
          linkUrl: `${STREAM_CHECKOUT}/${stream.id}/information`,
        };
      }

      if (isScheduled) {
        return {
          linkUrl: computeOrderStreamPath(stream),
        };
      }

      // Ended streams always link to watch-stream/video so that Googlebot
      // (unauthenticated) can discover and index the canonical video URL.
      // Access control (purchase gate / redirect) is enforced by the
      // watch-stream page itself at runtime.
      if (isEnded) {
        return {
          linkUrl: computeVideoWatchStreamPath(stream),
        };
      }

      if (!loggedUserId && !stream?.isFree) {
        return {
          linkUrl: computeOrderStreamPath(stream),
        };
      }

      if (!canWatch) {
        return {
          linkUrl: computeOrderStreamPath(stream),
        };
      }

      return {
        linkUrl: computeVideoWatchStreamPath(stream),
      };
    },
    [loggedUserId]
  );

  return getStreamNavigationProps;
}
